import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { TabProps } from 'types/tabs';

const TAB_VARIANTS = {
  default: {
    text: {
      active: 'text-cofeePrimary6',
      inactive: 'text-content'
    },
    count: {
      base: 'rounded-full px-3 py-0.5 text-sm font-normal *:ml-2',
      active: '!bg-primary100 text-cofeeNeutral10',
      inactive: 'bg-grey90 text-cofeeNeutral7'
    }
  },
  v2: {
    text: {
      active: 'text-cofeePrimary6',
      inactive: 'text-cofeeNeutral10'
    },
    count: {
      base: 'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold',
      active: 'bg-cofeePrimary2 text-cofeeNeutral10',
      inactive: 'bg-cofeeGreyBlue200 text-cofeeNeutral7'
    }
  }
} as const;

const Tabs = ({
  tabs,
  onTabSelect,
  selectedTab,
  className,
  tabClassName,
  countClassName,
  type = 'primary',
  variant = 'default',
  direction = 'horizontal',
  disabledTabIds = [],
  activeTabCountClassName = ''
}: TabProps) => {
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isSecondary = type === 'secondary';
  const variantStyles = TAB_VARIANTS[variant];

  useEffect(() => {
    if (selectedTab && tabs && !isSecondary) {
      const selectedIndex = tabs.findIndex((tab) => tab.id === selectedTab.id);
      const selectedTabElement = tabRefs.current[selectedIndex];

      if (selectedTabElement) {
        const { offsetLeft, offsetWidth } = selectedTabElement;
        setUnderlineStyle({
          left: offsetLeft,
          width: offsetWidth
        });
      }
    }
  }, [selectedTab, tabs, isSecondary]);

  const containerClass = isSecondary
    ? `relative flex ${className || ''} ${
        direction === 'vertical' ? 'flex-col' : 'flex-row'
      }`
    : `relative h-11 flex border-b border-cofeeGreyBlue300 ${className || ''} ${
        direction === 'vertical' ? 'flex-col' : 'flex-row'
      }`;

  const getTabItemClass = (
    isActive: boolean,
    index: number,
    isDisabled: boolean
  ) => {
    const cursorClass = isDisabled
      ? 'cursor-not-allowed text-cofeeNeutral7'
      : 'cursor-pointer text-cofeeNeutral10';
    if (isSecondary) {
      const commonClass = `${cursorClass} px-4 py-2.5 text-center text-sm font-medium ${
        index === 0 ? 'rounded-l-lg' : ''
      } ${index === tabs.length - 1 ? 'rounded-r-lg' : ''} ${
        direction === 'vertical'
          ? 'rounded-lg border-none'
          : 'border border-cofeeGreyBlue100'
      }`;

      return isActive
        ? ` bg-cofeePrimary3 ${commonClass}`
        : `bg-cofeeNeutral1 ${commonClass}`;
    }
    return `flex-end flex min-w-30 ${cursorClass} flex-col md:min-w-180`;
  };

  const getTabTextClass = (isActive: boolean) => {
    if (isSecondary) return '';
    return clsx(
      'flex justify-center gap-2 truncate px-4 py-3.5 pb-2 text-center text-base font-medium',
      isActive ? variantStyles.text.active : variantStyles.text.inactive
    );
  };

  return (
    <div className={containerClass}>
      {tabs?.map((tab, index) => {
        const isActive = selectedTab?.id === tab.id;
        const isDisabled = disabledTabIds.includes(tab.id);

        return isSecondary ? (
          <div
            role="presentation"
            className={`${getTabItemClass(isActive, index, isDisabled)} ${
              tabClassName || ''
            }`}
            key={tab.id}
            onClick={() => {
              if (!isDisabled) {
                onTabSelect?.(tab);
              }
            }}
          >
            {tab.label}
            {(tab.count || tab.count === 0) && (
              <span
                className={`ml-2 rounded-full bg-grey90 px-3 py-0.5 text-sm font-normal ${
                  countClassName || ''
                }`}
              >
                {tab.count}
              </span>
            )}
          </div>
        ) : (
          <div
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            role="presentation"
            className={getTabItemClass(isActive, index, isDisabled)}
            key={tab.id}
            onClick={() => {
              if (!isDisabled) {
                onTabSelect?.(tab);
              }
            }}
          >
            <div
              className={`${getTabTextClass(isActive)} ${tabClassName || ''}`}
              key={tab.id}
            >
              {tab.label}
              {(tab.count || tab.count === 0) && (
                <div
                  className={clsx(
                    variantStyles.count.base,
                    countClassName || '',
                    isActive
                      ? activeTabCountClassName || variantStyles.count.active
                      : variantStyles.count.inactive
                  )}
                >
                  {tab.count}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!isSecondary && (
        <div
          className="duration-250 absolute bottom-0 -mb-0.25 h-0.5 bg-cofeePrimary6 transition-all ease-out"
          style={{
            left: `${underlineStyle.left}px`,
            width: `${underlineStyle.width}px`
          }}
        />
      )}
    </div>
  );
};

export default Tabs;
